import React from 'react';
import { Modal } from 'react-bootstrap';
import { Spinner } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { usePlatforms } from '../../../contexts/PlatformsContext';

const LiveRankingModal = ({ show, handleClose, games, totalApps, loading, keyword }) => {
  const navigate = useNavigate();
  const { getPlatformDisplayName } = usePlatforms();

  const handleGameClick = (itemId) => {
    // Close the modal first
    handleClose();
    // Navigate to the game dashboard
    navigate(`/xr/app?id=${itemId}`);
  };

  // Already top-N, ranked, and merged with name/image_url/platform by the
  // /tracker/tracking/keyword_live_ranking API - no client-side join needed.
  const rankedItems = (games || []).map(g => ({
    itemId: g.item_id,
    rank: g.rank,
    name: g.name || 'Unknown Game',
    image_url: g.image_url || '',
    platform: g.platform || ''
  }));

  // totalApps is the true ranked-app count; games is capped to the top N
  // by the API, so anything beyond that is summarized rather than fetched.
  const moreGamesCount = Math.max(0, (totalApps || 0) - rankedItems.length);

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="live-ranking-modal">
      <Modal.Header closeButton style={{
        background: 'linear-gradient(135deg, #808080 0%, #764ba2 100%)',
        borderBottom: 'none',
        borderRadius: '12px 12px 0 0'
      }}>
        <Modal.Title style={{ color: '#fff', fontWeight: '600' }}>
          Live Ranking in {keyword}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{
        backgroundColor: '#2a2c38',
        color: '#fff',
        padding: '0',
        maxHeight: '70vh',
        overflowY: 'auto'
      }}>
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '40px 20px',
          }}>
            <Spinner color="light" />
          </div>
        ) : rankedItems.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#888'
          }}>
            No ranking data available
          </div>
        ) : (
          <div style={{ padding: '0' }}>
            {rankedItems.map((item, index) => (
              <div
                key={item.itemId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px 20px',
                  borderBottom: '1px solid #3a3c46',
                  transition: 'background-color 0.2s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#3a3c46';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                {/* Image Thumbnail - Clickable */}
                <div
                  style={{
                    width: '60px',
                    height: '45px',
                    marginRight: '16px',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    flexShrink: 0,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                  }}
                  onClick={() => handleGameClick(item.itemId)}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                  }}
                >
                  <img
                    src={item.image_url}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#4a4c56',
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: '#888'
                    }}
                  >
                    No Image
                  </div>
                </div>

                {/* Game Title */}
                <div style={{
                  flex: 1,
                  minWidth: 0
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#fff',
                    marginBottom: '4px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {item.name}
                  </div>
                  {item.platform && (
                    <div style={{
                      fontSize: '12px',
                      color: '#888'
                    }}>
                      {getPlatformDisplayName(item.platform)}
                    </div>
                  )}
                </div>

                {/* Rank Number */}
                <div style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#fff',
                  minWidth: '30px',
                  textAlign: 'right'
                }}>
                  {item.rank}
                </div>
              </div>
            ))}
            {moreGamesCount > 0 && (
              <div style={{
                textAlign: 'center',
                padding: '14px 20px',
                color: '#888',
                fontSize: '13px',
              }}>
                + {moreGamesCount} more game{moreGamesCount === 1 ? '' : 's'}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default LiveRankingModal; 